import { lazy, useEffect, useEffectEvent } from 'react';

import { sendNativeMessage } from 'hooks/useNativeListener';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { Toaster } from 'react-hot-toast';

import { usePlanPageTimeTracker } from '@/hooks/usePlanPageTimeTracker';
import { useTrackResourcePrint } from '@/hooks/useResourcePrintTracking';
import { useSearchSessionTracker } from '@/hooks/useSearchSessionTracker';
import { useTextInteractionTracking } from '@/hooks/useTextInteractionTracking';

import { isProgrammaticPrint } from '@/utils/print/programmaticPrintGuard';
import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import { PathwayModalRenderer } from '@/components/popups/PathwayModal';
import { PrintReport } from '@/components/print-report';

import Constants from '@/constants';

import MainContent from './Content/MainContent';
import Footer from './Footer/Footer';
import LayoutWrapper from './LayoutWrapper/LayoutWrapper';
import Navbar from './Navbar/Navbar';
import SmartBannerContainer from './SmartBanner/SmartBannerContainer';
import TrainTogetherModeWrapper from './TrainTogetherModeWrapper/TrainTogetherModeWrapper';
import { useAlertMessageBlocks } from './WebAlertBanner/useAlertMessageBlocks';
import WebAlertBannerContainer from './WebAlertBanner/WebAlertBannerContainer';

import { Routes } from '@/app';
import {
  documentAtom,
  isTridionDocAtom,
  translatedDocumentNameAtom,
} from '@/atoms/document';
import { isTridionPreviewAtom } from '@/atoms/is-tridion-preview';
import { trainTogetherModeAtom } from '@/atoms/trainTogetherMode';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { env } from '@/env';

const CommandMenu = lazy(
  () => import('@/components/developer-menu/engine/CommandMenu'),
);

const setIsBeforeNavigateAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('isBeforeNavigate'),
);

const isTrainTogetherModeAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('isTrainTogetherMode'),
);

const Layout = () => {
  const setIsBeforeNavigate = useSetAtom(setIsBeforeNavigateAtom);
  const isTrainTogetherMode = useAtomValue(isTrainTogetherModeAtom);
  const trackResourcePrint = useTrackResourcePrint();
  const isTridion = useAtomValue(isTridionDocAtom);
  const isTridionPreview = useAtomValue(isTridionPreviewAtom);
  const { document: currentDocument } = useAtomValue(documentAtom);
  const docName = useAtomValue(translatedDocumentNameAtom);

  useSearchSessionTracker();
  usePlanPageTimeTracker();
  useTextInteractionTracking();

  const routeMatches = useRouteMatcher();
  const isSignalHeadlessView = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.SignalPath,
  );

  useAlertMessageBlocks({ enabled: !isSignalHeadlessView });

  const hasSidenav = !isTrainTogetherMode && !window.native.isNative;

  const userLanguage = useAtomValue(userPrefsAtom).language;

  const sendLanguageChangedEvent = useEffectEvent(() => {
    sendNativeMessage({
      type: 'languageChanged',
      payload: userLanguage,
    });
  });

  useEffect(() => {
    sendLanguageChangedEvent();
    // https://github.com/kutlugsahin/react-smooth-dnd/issues/75
    // This library hasn't been updated since 2020.
    const cleanClasses = () => {
      document.body.classList.remove(
        'smooth-dnd-no-user-select',
        'smooth-dnd-disable-touch-action',
      );
    };
    document.addEventListener('touchend', cleanClasses, false);
    return () => {
      document.removeEventListener('touchend', cleanClasses, false);
    };
  }, []);

  useEffect(() => {
    setIsBeforeNavigate(false);
  }, [setIsBeforeNavigate]);

  // File → Print on a mounted Tridion doc (skip programmatic button/Cmd+P prints).
  useEffect(() => {
    const handleBeforePrint = () => {
      if (isProgrammaticPrint()) return;
      // Preview mounts Tridion UI without updating documentAtom — skip stale ids.
      if (isTridionPreview) return;
      if (!isTridion || !currentDocument?.id) return;
      // Atom can stay TRIDION after navigation; only track when content is mounted.
      if (!document.querySelector('[data-tridion-content]')) return;

      trackResourcePrint('browser', docName ?? currentDocument.name);
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, [
    trackResourcePrint,
    isTridion,
    isTridionPreview,
    currentDocument,
    docName,
  ]);

  return !isSignalHeadlessView ? (
    <LayoutWrapper>
      <TrainTogetherModeWrapper>
        <WebAlertBannerContainer />
        <Navbar className={cn(isTrainTogetherMode && 'hidden!')} />
        <div
          className={cn(
            'bg-base-0 min-h-screen p-0 md:min-h-screen!',
            hasSidenav
              ? 'md:ml-14! lg:ml-(--sidenav-width)!'
              : 'ml-0! lg:ml-0!',
          )}
        >
          {env.REACT_APP_ENV !== 'prod' && <CommandMenu />}
          <SmartBannerContainer />
          <MainContent>
            {/*
              Toasts and message blocks stack at the top of the content column,
              just under the banner, overlaying the page and its header. react-hot-toast
              sets top/left/right inline, hence the important modifiers.
            */}
            <Toaster
              position="top-center"
              containerStyle={{
                top: 'calc(var(--web-alert-banner-height, 0px) + 8px)',
              }}
              containerClassName={cn(
                'max-w-content inset-x-4! mx-auto',
                hasSidenav &&
                  'md:left-18! lg:left-[calc(var(--sidenav-width)+1rem)]!',
              )}
            />
            <div style={{ marginTop: 'var(--web-alert-banner-height, 0px)' }}>
              <Routes />
            </div>
          </MainContent>
          <Footer />
        </div>
        <PrintReport />
      </TrainTogetherModeWrapper>
    </LayoutWrapper>
  ) : (
    <MainContent>
      <Toaster position="top-center" />
      <Routes />
      <PathwayModalRenderer />
    </MainContent>
  );
};

export default Layout;
